import React, { useState, useEffect, useContext, useMemo } from "react";
import {
	Box,
	Card,
	CardContent,
	Grid,
	InputLabel,
	TextField,
} from "../../../common/MaterialComponent";
import PickLanguageWidgetModel from "../../../models/RequestForHelpTemplates/PickLanguageWidgetModel";
import {
	IVRFlowWizardContext,
	FlowWizardState,
	WidgetPropertyType,
	GetNextActionWidgetList,
	WizardStepperProp,
	IsAudioConfigurationAllowed,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import {
	IVRFlowTypeEnum,
	TemplateType,
	WidgetTypeEnum,
} from "../../../common/enum";
import {
	ResponseMapWidget,
	ResponseMapPropertyType,
} from "../ResponseMapWidget/ResponseMapWidget";
import ResponseMapModel from "../../../models/RequestForHelpTemplates/ResponseMapModel";
import { INextActionWidget } from "../../../common/interfaces";
import { NextActionSelection } from "../NextActionSelection/NextActionSelection";
import { FailureActionConfiguration } from "../FailureActionConfiguration/FailureActionConfiguration";
import "../IVRFlowConfiguration/IVRFlowConfiguration.css";
import FileUpload from "../../BatchNotification/FileUpload/FileUpload";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export const PickLanguageWidget: React.FC<WizardStepperProp> = ({
	ivrFlowType,
	highlightRequiredDetail,
}) => {
	const [
		pickLanguageWidgetDetail,
		setPickLanguageWidgetDetail,
	] = useState<PickLanguageWidgetModel>(new PickLanguageWidgetModel());
	const createNewWidgetId = "CreateNewWidget";

	const { state, dispatch } = useContext(IVRFlowWizardContext);
	const strings = useGetComponentStringsContext().PickLanguageWidget;

	const updateWidgetDetail = (
		propertyName: string,
		propertyValue: WidgetPropertyType
	) => {
		// dispatch action to update widget detail
		dispatch({
			type: "UPDATE_WIDGET_DETAIL",
			payload: {
				widgetId: pickLanguageWidgetDetail.id,
				widgetType: pickLanguageWidgetDetail.type as WidgetTypeEnum,
				propertyName: propertyName,
				propertyValue: propertyValue,
			},
		});
	};

	const onWidgetDetailChange = (
		value: string | number,
		propertyName: string
	) => {
		const updatedInputValue =
			typeof value === "string" ? Shared.getSanitizedInputValue(value) : value;
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		(pickLanguageObj as any)[propertyName] = updatedInputValue;
		setPickLanguageWidgetDetail(pickLanguageObj);

		updateWidgetDetail(propertyName, updatedInputValue);
	};

	useEffect(() => {
		const widgetDetail = (state as FlowWizardState).wizardConfig
			.pickLanguageWidgetDetail;
		setPickLanguageWidgetDetail(widgetDetail!);
		// disabled eslint rule as useEffect needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addNewResponseMapDetail = () => {
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		pickLanguageObj.responseMap.push(new ResponseMapModel());
		setPickLanguageWidgetDetail(pickLanguageObj);
		updateWidgetDetail("responseMap", pickLanguageObj.responseMap);
	};

	const updateResponseMapDetail = (
		value: string,
		propertyName: ResponseMapPropertyType,
		index: number
	) => {
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		pickLanguageObj.responseMap[index][propertyName] =
			value === createNewWidgetId ? "" : value;
		setPickLanguageWidgetDetail(pickLanguageObj);
		updateWidgetDetail("responseMap", pickLanguageObj.responseMap);
		if (value === createNewWidgetId) {
			handleCreateWidgetAction();
		}
	};

	const deleteResponseMapDetail = (index: number) => {
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		pickLanguageObj.responseMap.splice(index, 1);

		setPickLanguageWidgetDetail(pickLanguageObj);
		updateWidgetDetail("responseMap", pickLanguageObj.responseMap);
		// dispatch action to update multi language/audio detail
		dispatch({
			type: "RESET_LANGUAGE_AUDIO_DETAIL",
			payload: {},
		});
	};

	const handleNextActionSelection = (value: string) => {
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		pickLanguageObj.nextAction = value === createNewWidgetId ? "" : value;
		setPickLanguageWidgetDetail(pickLanguageObj);
		updateWidgetDetail("nextAction", pickLanguageObj.nextAction);
		if (value === createNewWidgetId) {
			handleCreateWidgetAction();
		}
	};

	const selectedLanguageList = useMemo(
		() => () => {
			const langList: string[] = pickLanguageWidgetDetail.responseMap
				.map((r) => r.variableValue)
				.filter((lang) => lang !== "");
			return Array.from(new Set(langList));
		},
		[pickLanguageWidgetDetail.responseMap]
	);

	const nextActionWidgetList = () => {
		// pass stepper content list and selected widget type
		const widgetList: INextActionWidget[] = GetNextActionWidgetList(
			(state as FlowWizardState).stepperContent
		);
		return widgetList;
	};

	const handleCreateWidgetAction = () => {
		// Create New Widget option is selected, dispatch action to open widget selection menu
		dispatch({
			type: "HANDLE_CREATE_WIDGET",
			payload: {},
		});
	};

	const handleAudioFileChange = (obj: AudioFileModel, propertyName: string) => {
		const pickLanguageObj: PickLanguageWidgetModel = {
			...pickLanguageWidgetDetail,
		};
		(pickLanguageObj as any)[propertyName]!.value = obj.value;
		(pickLanguageObj as any)[propertyName]!.fileName = obj.fileName;
		(pickLanguageObj as any)[propertyName]!.audioDuration = obj.audioDuration;
		setPickLanguageWidgetDetail(pickLanguageObj);
		updateWidgetDetail("audioFile", pickLanguageObj.audioFile!);
	};

	return (
		<>
			<Card variant="outlined">
				<CardContent>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<TextField
								label={strings.messageText}
								value={pickLanguageWidgetDetail.messageText}
								onChange={(event) =>
									onWidgetDetailChange(
										event.target.value as string,
										"messageText"
									)
								}
								multiline
								rows={4}
								fullWidth
								required
								error={
									!pickLanguageWidgetDetail.messageText &&
									highlightRequiredDetail
								}
								helperText={
									!pickLanguageWidgetDetail.messageText &&
									highlightRequiredDetail
										? strings.messageTextErrorMessage
										: ""
								}
							/>
						</Grid>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<Grid item xs={12}>
								<Box pt={2}>
									<InputLabel>{strings.audioFile}</InputLabel>
								</Box>
								<Box mt={1} mb={2}>
									<FileUpload
										message={pickLanguageWidgetDetail.audioFile!.value}
										fileName={pickLanguageWidgetDetail.audioFile!.fileName}
										index="PickLanguage_AudioFile"
										audioDuration={
											pickLanguageWidgetDetail.audioFile!.audioDuration
										}
										removeUploadedFile={() =>
											handleAudioFileChange(new AudioFileModel(), "audioFile")
										}
										partitionKey={(state as FlowWizardState).partitionKey}
										rowKey={(state as FlowWizardState).rowKey}
										templateType={TemplateType.RequestForHelp}
										onFileChange={(
											fileUrl: string,
											fileName: string,
											audioDuration: number
										) => {
											handleAudioFileChange!(
												Object.assign(new AudioFileModel(), {
													value: fileUrl,
													fileName,
													audioDuration,
												}),
												"audioFile"
											);
										}}
									></FileUpload>
								</Box>
							</Grid>
						)}
						{ivrFlowType !== IVRFlowTypeEnum.USSD && (
							<Grid item xs={12}>
								<TextField
									label={strings.timeout}
									value={pickLanguageWidgetDetail.timeout}
									onChange={(event) =>
										onWidgetDetailChange(
											event.target.value ? Number(event.target.value) : "",
											"timeout"
										)
									}
									fullWidth
									type="number"
									required
									error={
										!pickLanguageWidgetDetail.timeout && highlightRequiredDetail
									}
									helperText={
										!pickLanguageWidgetDetail.timeout && highlightRequiredDetail
											? strings.timeoutErrorMessage
											: ""
									}
								/>
							</Grid>
						)}
						<ResponseMapWidget
							responseMap={pickLanguageWidgetDetail.responseMap}
							widgetList={nextActionWidgetList()}
							addNewResponseMapDetail={addNewResponseMapDetail}
							updateResponseMapDetail={updateResponseMapDetail}
							deleteResponseMapDetail={deleteResponseMapDetail}
							ivrFlowType={ivrFlowType}
							highlightRequiredDetail={highlightRequiredDetail}
						/>
						<Grid item xs={12}>
							<TextField
								label={strings.maxRetries}
								value={pickLanguageWidgetDetail.maxRetries}
								onChange={(event) =>
									onWidgetDetailChange(
										event.target.value ? Number(event.target.value) : "",
										"maxRetries"
									)
								}
								fullWidth
								type="number"
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={strings.retryMessageText}
								value={pickLanguageWidgetDetail.retryMessage}
								onChange={(event) =>
									onWidgetDetailChange(
										event.target.value as string,
										"retryMessage"
									)
								}
								multiline
								rows={4}
								fullWidth
							/>
						</Grid>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<Grid item xs={12}>
								<Box pt={2}>
									<InputLabel>{strings.retryAudioFile}</InputLabel>
								</Box>
								<Box mt={1} mb={2}>
									<FileUpload
										message={pickLanguageWidgetDetail.retryAudioFile!.value}
										index="PickLanguage_RetryAudioFile"
										audioDuration={
											pickLanguageWidgetDetail.retryAudioFile!.audioDuration
										}
										fileName={pickLanguageWidgetDetail.retryAudioFile!.fileName}
										removeUploadedFile={() =>
											handleAudioFileChange(
												new AudioFileModel(),
												"retryAudioFile"
											)
										}
										partitionKey={(state as FlowWizardState).partitionKey}
										rowKey={(state as FlowWizardState).rowKey}
										templateType={TemplateType.RequestForHelp}
										onFileChange={(
											fileUrl: string,
											fileName: string,
											audioDuration: number
										) => {
											handleAudioFileChange!(
												Object.assign(new AudioFileModel(), {
													value: fileUrl,
													fileName: fileName,
													audioDuration: audioDuration,
												}),
												"retryAudioFile"
											);
										}}
									></FileUpload>
								</Box>
							</Grid>
						)}
						<FailureActionConfiguration
							failureActionType={pickLanguageWidgetDetail.failureActionType}
							failureDefaultValue={pickLanguageWidgetDetail.failureDefaultValue}
							failureMessage={pickLanguageWidgetDetail.failureMessage}
							failureAudioFile={pickLanguageWidgetDetail.failureAudioFile}
							selectedLanguageList={selectedLanguageList()}
							partitionKey={(state as FlowWizardState).partitionKey}
							rowKey={(state as FlowWizardState).rowKey}
							handleFailureActionConfigChange={(value: string, type: string) =>
								onWidgetDetailChange(value, type)
							}
							handleAudioFileChange={(
								obj: AudioFileModel,
								propertyName: string
							) => {
								handleAudioFileChange(obj, propertyName);
							}}
							widgetType={pickLanguageWidgetDetail.type}
							ivrFlowType={ivrFlowType}
							highlightRequiredDetail={highlightRequiredDetail}
						/>
						<Grid item xs={12} className="responseMapGridSpace">
							<NextActionSelection
								widgetList={nextActionWidgetList()}
								selectedWidget={pickLanguageWidgetDetail.nextAction}
								onNextActionChange={(value: string) =>
									handleNextActionSelection(value)
								}
								highlightRequiredDetail={highlightRequiredDetail}
								isValidationRequired={
									pickLanguageWidgetDetail.responseMap.filter(
										(r) => r.nextAction === ""
									).length > 0
								}
							/>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</>
	);
};
